import type {
  DailyChallenge,
  DailyChallengeProgress,
  DailyChallengeState,
} from "../types/dailyChallenges";
import type { GraphData } from "../types/gameTypes";
import {
  DeterministicSelector,
  type ChallengeSelector,
} from "../game/challengeSelector";
import { Logger } from "../utils/logger";
import { unifiedDataStore } from "./UnifiedDataStore";

export class DailyChallengesService {
  private static instance: DailyChallengesService;

  private graph: GraphData | null = null;
  private selector: ChallengeSelector = new DeterministicSelector();
  private cache = new Map<string, DailyChallenge>();

  private constructor() {
    // No pre-generated data to load — challenges are generated on-demand.
  }

  public static getInstance(): DailyChallengesService {
    if (!DailyChallengesService.instance) {
      DailyChallengesService.instance = new DailyChallengesService();
    }
    return DailyChallengesService.instance;
  }

  /**
   * Inject the word graph used for on-demand challenge generation.
   * Clears the internal cache so fresh challenges are generated for the new graph.
   */
  public setGraph(graph: GraphData | null): void {
    this.graph = graph;
    this.cache.clear();
  }

  /**
   * Get today's date in YYYY-MM-DD format using EST timezone.
   * Daily challenges reset at 12:00 AM EST.
   */
  private getTodayString(): string {
    const now = new Date();

    // The correct way to get a date string in a specific timezone is to format it
    // directly, not to create a new Date object from a localized string, which
    // is unreliable.
    const formatter = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: "America/New_York",
    });

    return formatter.format(now);
  }

  /**
   * Get the daily challenge for a specific date.
   * Cache is checked first — this lets setTestChallenges() override generation
   * and also avoids re-running the selector for repeated calls with the same date.
   */
  public getChallengeForDate(date: string): DailyChallenge | null {
    const cached = this.cache.get(date);
    if (cached) return cached; // cache hit FIRST (so setTestChallenges works even with null graph)

    if (!this.graph) return null;

    const c = this.selector.select(date, this.graph);
    if (c) this.cache.set(date, c);
    return c;
  }

  /**
   * Get today's daily challenge.
   */
  public getTodaysChallenge(): DailyChallenge | null {
    return this.getChallengeForDate(this.getTodayString());
  }

  /**
   * Check if user has completed today's challenge
   */
  public async hasCompletedTodaysChallenge(): Promise<boolean> {
    const todaysChallenge = this.getTodaysChallenge();
    console.log(
      "🔍 DailyChallengesService: hasCompletedTodaysChallenge called:",
      {
        todaysChallengeExists: !!todaysChallenge,
        challengeId: todaysChallenge?.id,
      },
    );

    if (!todaysChallenge) {
      console.log(
        "🔍 DailyChallengesService: No challenge for today, returning false",
      );
      return false; // No challenge available for today
    }

    const progress = await unifiedDataStore.getDailyChallengeProgress();
    console.log("🔍 DailyChallengesService: Progress data:", {
      allProgressKeys: Object.keys(progress),
      targetChallengeId: todaysChallenge.id,
      targetProgress: progress[todaysChallenge.id],
      isCompleted: progress[todaysChallenge.id]?.completed,
    });

    // Use the challenge ID as the key, not the date
    const isCompleted = progress[todaysChallenge.id]?.completed || false;
    console.log("🔍 DailyChallengesService: Final completion result:", {
      challengeId: todaysChallenge.id,
      isCompleted,
      progressExists: !!progress[todaysChallenge.id],
    });
    return isCompleted;
  }

  /**
   * Check if user has completed a specific daily challenge by ID
   */
  public async hasCompletedSpecificChallenge(
    challengeId: string,
  ): Promise<boolean> {
    console.log(
      "🔍 DailyChallengesService: hasCompletedSpecificChallenge called:",
      { challengeId },
    );

    const progress = await unifiedDataStore.getDailyChallengeProgress();
    const isCompleted = progress[challengeId]?.completed || false;

    console.log(
      "🔍 DailyChallengesService: Specific challenge completion result:",
      {
        challengeId,
        isCompleted,
        progressExists: !!progress[challengeId],
      },
    );

    return isCompleted;
  }

  /**
   * Get all daily challenge progress
   */
  public async getDailyChallengeProgress(): Promise<
    Record<string, DailyChallengeProgress>
  > {
    return await unifiedDataStore.getDailyChallengeProgress();
  }

  /**
   * Save daily challenge progress
   */
  public async saveDailyChallengeProgress(
    challengeId: string,
    progress: Omit<DailyChallengeProgress, "challengeId" | "completed"> & {
      status: "won" | "given_up";
    },
  ): Promise<void> {
    const fullProgress: DailyChallengeProgress = {
      challengeId,
      completed: true, // Mark as completed regardless of status (restored behavior)
      ...progress,
    };
    await unifiedDataStore.updateDailyChallengeProgress(
      challengeId,
      fullProgress,
    );
  }

  /**
   * Get remaining free games for today
   */
  public async getRemainingFreeGames(): Promise<number> {
    return await unifiedDataStore.getRemainingFreeGames();
  }

  /**
   * Consume a free game
   */
  public async consumeFreeGame(): Promise<number> {
    return await unifiedDataStore.consumeFreeGame();
  }

  /**
   * Check if user has game access (always true in local-only mode)
   */
  public async hasGameAccess(): Promise<boolean> {
    // Local-only mode - everyone has full access
    return true;
  }

  /**
   * Get the current daily challenge state
   */
  public async getDailyChallengeState(): Promise<DailyChallengeState> {
    const todaysChallenge = this.getTodaysChallenge();
    const hasPlayedToday = await this.hasCompletedTodaysChallenge();
    const remainingFreeGames = await this.getRemainingFreeGames();
    const hasAccess = await this.hasGameAccess();
    const progress = await this.getDailyChallengeProgress();

    return {
      todaysChallenge,
      hasPlayedToday,
      remainingFreeGames,
      hasAccess,
      progress,
    };
  }

  /**
   * Warm the per-date cache for today + the current calendar month so the
   * first calendar open is instant (~15ms for a month with generator v2).
   * Cooperative: yields to the event loop between dates. Idempotent (cache-first) and
   * fault-tolerant (a date that fails to generate is skipped).
   */
  public async warmCurrentWindow(): Promise<void> {
    for (const dateStr of this.getCurrentWindowDates()) {
      this.warmDate(dateStr);
      // Yield so the loading overlay can paint/animate between dates.
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }

  /** Every date of the current month ("YYYY-MM-DD", America/New_York). */
  public getCurrentWindowDates(): string[] {
    const today = this.getTodayString();
    const [year, month] = today.split("-").map(Number);
    // Date.UTC(year, month, 0) = last day of the 1-based `month`.
    const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const dates: string[] = [];
    for (let day = 1; day <= daysInMonth; day++) {
      dates.push(
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      );
    }
    return dates;
  }

  /** Generate and cache one date; a failure is skipped, never thrown. */
  public warmDate(dateStr: string): void {
    try {
      this.getChallengeForDate(dateStr); // cache-first; caches non-null results
    } catch {
      // Skip a date that fails to generate; never block the warm.
    }
  }

  /**
   * Get challenges for a date range (for calendar view).
   * Iterates each calendar date from startDate to endDate inclusive.
   * Capped at 366 iterations to avoid pathological ranges.
   */
  public getChallengesInRange(
    startDate: string,
    endDate: string,
  ): DailyChallenge[] {
    const results: DailyChallenge[] = [];
    const start = new Date(startDate + "T00:00:00Z");
    const end = new Date(endDate + "T00:00:00Z");
    const current = new Date(start);
    let iterations = 0;

    while (current <= end && iterations < 366) {
      const dateStr = current.toISOString().split("T")[0];
      const challenge = this.getChallengeForDate(dateStr);
      if (challenge) results.push(challenge);
      current.setUTCDate(current.getUTCDate() + 1);
      iterations++;
    }

    return results;
  }

  /**
   * Get all available challenges (last 60 days through today, for calendar view).
   */
  public getAllChallenges(): DailyChallenge[] {
    const today = this.getTodayString();
    const todayDate = new Date(today + "T00:00:00Z");
    const sixtyDaysAgo = new Date(todayDate);
    sixtyDaysAgo.setUTCDate(sixtyDaysAgo.getUTCDate() - 60);
    const startDate = sixtyDaysAgo.toISOString().split("T")[0];
    return this.getChallengesInRange(startDate, today);
  }

  /**
   * Check if a challenge is available (not in the future)
   */
  public isChallengeAvailable(date: string): boolean {
    const today = this.getTodayString();
    return date <= today;
  }

  /**
   * Reset all daily challenge data (for testing purposes)
   */
  public async resetDailyChallengeData(): Promise<void> {
    // Reset the daily challenges section in unified data
    const data = await unifiedDataStore.getData();
    data.dailyChallenges = {
      progress: {},
      freeGamesRemaining: 1,
      lastResetDate: this.getTodayString(),
    };
    await unifiedDataStore.saveData();
    Logger.debug("Daily challenge data cleared");
  }

  /**
   * Set test challenges by seeding the cache.
   * Any subsequent call to getChallengeForDate(c.date) will return the cached
   * value without invoking the generator — even when no graph is set.
   */
  public setTestChallenges(testChallenges: DailyChallenge[]): void {
    for (const c of testChallenges) {
      this.cache.set(c.date, c);
    }
    Logger.debug("Test challenges set:", testChallenges.length);
  }
}

// Export singleton instance
export const dailyChallengesService = DailyChallengesService.getInstance();
